import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  LinearProgress,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import axiosInstance from "../../services/axiosInstance";
import GlassCard from "../Cards/GlassCard";
import { SUCCESS, WARNING, DANGER } from "../../utils/colors";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const WEEK_PRESETS = [4, 8];

const cardConfig = [
  {
    key: "activelySolving",
    title: "Actively Solving",
    description: "Students with consistent coding activity",
    tooltip: "Students at or above the 75th percentile of submission activity",
    color: SUCCESS,
  },
  {
    key: "moderatelySolving",
    title: "Moderately Solving",
    description: "Students with occasional coding activity",
    tooltip: "Students between the 40th and 75th percentile of submission activity",
    color: WARNING,
  },
  {
    key: "needsImprovement",
    title: "Needs Improvement",
    description: "Students with minimal or no activity",
    tooltip: "Students below the 40th percentile of submission activity",
    color: DANGER,
  },
];

const LeetCodeSegmentationCards = ({ batchId }) => {
  const [weeks, setWeeks] = useState(8);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [segmentation, setSegmentation] = useState(null);

  useEffect(() => {
    if (!batchId) {
      setSegmentation(null);
      setError("Batch context is required for segmentation.");
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError("");

    axiosInstance
      .get("/analytics/leetcode/segmentation", { params: { weeks, batchId } })
      .then(({ data }) => {
        if (!active) return;
        setSegmentation(data || null);
      })
      .catch((err) => {
        if (!active) return;
        setSegmentation(null);
        setError(err?.response?.data?.message || "Failed to load segmentation.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [weeks, batchId]);

  const items = useMemo(() => {
    if (!segmentation) return [];
    const segmentAverages = cardConfig.map((cfg) =>
      Number(segmentation?.[cfg.key]?.avgSubmissions) || 0
    );
    const maxAverage = Math.max(...segmentAverages, 0);

    return cardConfig.map((cfg) => {
      const segment = segmentation[cfg.key] || { count: 0, percentage: 0 };
      return {
        ...cfg,
        count: Number(segment.count) || 0,
        percentage: Number(segment.percentage) || 0,
        avgProblemsSolved: Number(segment.avgSubmissions) || 0,
        strengthPct: maxAverage > 0
          ? Number((((Number(segment.avgSubmissions) || 0) / maxAverage) * 100).toFixed(1))
          : 0,
      };
    });
  }, [segmentation]);

  return (
    <GlassCard hoverable={false} sx={{ borderRadius: 3, p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          LeetCode Activity Segmentation
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={weeks}
          onChange={(_, next) => {
            if (next) setWeeks(next);
          }}
          sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.25, fontWeight: 600 } }}
        >
          {WEEK_PRESETS.map((w) => (
            <ToggleButton key={w} value={w}>
              Last {w} weeks
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {loading && (
        <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={30} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid item xs={12} md={4} key={item.key}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: item.color,
                  borderRadius: 2,
                  p: 1.5,
                  height: "100%",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: item.color }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    {item.title}
                    <Tooltip title={item.tooltip} arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    </Tooltip>
                  </Box>
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25 }}>
                  {item.count}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {item.percentage}% of students
                </Typography>

                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Avg Problems Solved: {item.avgProblemsSolved}
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, item.percentage))}
                  sx={{
                    height: 7,
                    borderRadius: 999,
                    mb: 1,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": { bgcolor: item.color },
                  }}
                />
                {/* <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, mb: 0.75 }}>
                  Segment share
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, item.strengthPct))}
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    mb: 1,
                    bgcolor: "action.hover",
                    "& .MuiLinearProgress-bar": { bgcolor: item.color },
                  }}
                /> */}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Relative strength: {item.strengthPct}%
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem" }}>
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </GlassCard>
  );
};

export default LeetCodeSegmentationCards;
